import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { markSupportShownOnce } from 'Design-system/app/static/scripts/support-widget';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { isSupportAvailable } from 'Douleutaras/redux/data/settings/supportSlice';
import { useSalesForceChat } from 'Douleutaras/providers/chat/hooks/useSalesForceChat';
import SupportSheet from 'Douleutaras/functional-components/support-widget/SupportSheet';
import {
  canAskPageForSupportSheet,
  requestPageSupportSheet
} from 'Douleutaras/functional-components/support-widget/requestPageSupportSheet';
import { formatSupportHours } from 'Douleutaras/functional-components/support-widget/supportHours';
import { supportUserProperties } from 'Douleutaras/functional-components/support-widget/floatingSupportTracking';
import { useAB } from 'Douleutaras/app/lib/ab';
import {
  SupportChannel,
  SupportDismissMethod,
  SupportEventProperties,
  SupportSurface,
  SupportTrigger
} from 'Douleutaras/app/lib/posthog/posthog.types';

import JobFormExitIntentView from './JobFormExitIntentView';

const MS_PER_SECOND = 1000;

export interface JobFormExitIntentProps {
  /** Return to the job form with everything the customer had already filled in. */
  onContinue(): void;
  /** Abandon the request — the customer lands wherever the surface's close leads. */
  onCancel(): void;
}

/**
 * Exit intent on the job form (#5622) — availability, tracking, and the support
 * sheet, wired around the presentational panel.
 *
 * Availability comes from the #5608 endpoint and is read as `state` only. Outside
 * working hours, and when the Salesforce kill switch is off, that state is not
 * `available`, so the help block is absent. This component contains no
 * availability logic of its own and must not grow any.
 *
 * When the block is absent, this panel is not a support surface: it is the job
 * form's retention modal and nothing else. It therefore reports no
 * `Support Widget *` event at all — see the comment on the `Shown` effect.
 */
export const JobFormExitIntent = ({ onContinue, onCancel }: JobFormExitIntentProps) => {
  const { t } = useTranslation();
  const support = useAppSelector(state => state.support);
  const user = useAppSelector(selectCurrentUser);
  const chat = useSalesForceChat();
  const {
    captureSupportWidgetShown,
    captureSupportChannelSelected,
    captureSupportWidgetDismissed
  } = useAB();

  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const supportAvailable = isSupportAvailable(support);

  // Keep this bag stable per surface: any varying property would split one
  // surface's impressions across bags.
  const eventProperties: SupportEventProperties = {
    surface: SupportSurface.EXIT_INTENT_JOB_FORM,
    trigger: SupportTrigger.EXIT_CLOSE,
    support_available: supportAvailable,
    ...supportUserProperties(user)
  };

  // Read by the unmounted handler, which outlives the render that recorded them.
  const latest = useRef({ eventProperties, captureSupportWidgetDismissed });
  // A channel pick is not a dismissal, so a surface that produced one must not
  // also report, `Support Widget Dismissed`.
  const channelSelected = useRef(false);
  const outcome = useRef<SupportDismissMethod>(undefined);
  // When this surface presented a support entry point (0: never). Nothing shown,
  // nothing to dismiss; the gap is the Dismissed event's `seconds_open`.
  const shownAt = useRef(0);
  // The page that frames this form opened its own widget for us, so the episode — and
  // the events that end it — belong to that widget from here on (#5622).
  const delegated = useRef(false);
  // The embedded sheet has no launcher, so the core has nothing to hand focus back to
  // on close. The banner CTA that opened it is the right place.
  const focusBeforeSheet = useRef<HTMLElement>(undefined);
  // The page's answer can land after the panel is gone — «Continue» and the × both
  // unmount this inside the request's own timeout window.
  const mounted = useRef(true);

  latest.current = { eventProperties, captureSupportWidgetDismissed };

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  // The exit modal appearing *is* this surface's impression — there is no separate
  // "Clicked" step, so `Shown` fires on mount rather than on a launcher reveal.
  //
  // Out of working hours the help block is absent, so nothing support-related is
  // ever on screen, and the surface stays silent. Reporting it anyway would put
  // plain form-abandonment volume into the support funnel — the exit modal opens
  // for every abandonment — and would give `Dismissed` a denominator of
  // impressions that never existed. Unmet demand out of hours is what the
  // always-on BSDIR widget measures, with `support_available: false`; here there
  // is no entry point to express it.
  //
  // This also settles the first render: availability is `false` until the #5608
  // fetch lands, and firing then would emit a second `Shown` with a different bag.
  //
  // At most one `Shown` per surface per session (tracking plan): `capture`'s dedup
  // is in memory and only compares with the last bag, so it does not survive a
  // reload or a Shown from another surface. `shownAt` is still set — a panel that
  // is on screen can still be dismissed.
  useEffect(() => {
    if (!supportAvailable) {
      return;
    }

    shownAt.current = Date.now();

    if (markSupportShownOnce(SupportSurface.EXIT_INTENT_JOB_FORM)) {
      captureSupportWidgetShown(eventProperties);
    }
  }, [supportAvailable]);

  // Report the dismissal once, on the way out, whichever way the customer left:
  // the two buttons, the modal's ×, or navigating away. Only «Continue» returns to
  // the form — every other path, the × included, abandons it — so that is the default.
  useEffect(() => () => {
    if (!shownAt.current || channelSelected.current || delegated.current) {
      return;
    }

    latest.current.captureSupportWidgetDismissed({
      ...latest.current.eventProperties,
      dismiss_method: outcome.current || SupportDismissMethod.CLOSE_FORM,
      seconds_open: Math.round((Date.now() - shownAt.current) / MS_PER_SECOND)
    });
  }, []);

  const openLocalSheet = () => {
    focusBeforeSheet.current = document.activeElement as HTMLElement;
    setIsSheetOpen(true);
  };

  const closeLocalSheet = () => {
    setIsSheetOpen(false);
    focusBeforeSheet.current?.focus?.();
    focusBeforeSheet.current = undefined;
  };

  const handleChannelSelected = (channel: SupportChannel) => {
    channelSelected.current = true;
    captureSupportChannelSelected({ ...eventProperties, channel });
  };

  // Prefer the widget the surrounding page already has: on BSDIR this panel sits in an
  // iframe, so a sheet rendered in here is boxed into that rectangle and boots a second
  // Salesforce chat behind the one the page has. Where there is no such page — ProList,
  // a Django bundle without the listener, a widget that declined to open — the local
  // sheet answers instead, so the banner always does something.
  const handleRequestHelp = () => {
    // No page to ask means no round trip: the sheet opens on the click itself.
    if (!canAskPageForSupportSheet()) {
      openLocalSheet();

      return;
    }

    void requestPageSupportSheet()
      .then(opened => {
        // Re-answered per request: the page can open its widget once and decline the
        // next ask (it persists a dismissal), and a stuck flag would suppress this
        // surface's Dismissed event for the rest of its life.
        delegated.current = opened;

        if (!opened && mounted.current) {
          openLocalSheet();
        }
      });
  };

  return (
    <>
      <JobFormExitIntentView
        supportAvailable={supportAvailable}
        onContinue={() => {
          outcome.current = SupportDismissMethod.CONTINUE_JOB;
          onContinue();
        }}
        onCancel={() => {
          outcome.current = SupportDismissMethod.CLOSE_FORM;
          onCancel();
        }}
        onRequestHelp={handleRequestHelp}
      />

      { supportAvailable &&
        <SupportSheet
          isOpen={isSheetOpen}
          chatEnabled={support.entities.channels.chat.enabled}
          callEnabled={support.entities.channels.call.enabled}
          phone={support.entities.channels.call.phone}
          hoursLabel={formatSupportHours(support.entities.schedule, t)}
          // The provider resolves through a dynamic import; show the button busy
          // rather than rendering a channel whose click would do nothing.
          chatBusy={!chat}
          onOpenChat={() => { void chat?.open(); }}
          onChannelSelected={handleChannelSelected}
          // Closing the sheet returns to the exit panel — the surface is still up,
          // so this is not a dismissal. Leaving the panel reports that.
          onClose={closeLocalSheet}
        />
      }
    </>
  );
};

export default JobFormExitIntent;
